import { Button, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import Iconify from 'src/components/iconify';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import { useRemindersScope } from 'src/sections/@dashboard/visit/hooks/useRemindersScope';
import { useVisitScope } from 'src/sections/@dashboard/visit/hooks/useVisitScope';
import { CreateReminderDto, UpdateReminderDto } from 'woofwoof-api';
import ProfileRemindersDeleteModal from './ProfileRemindersDeleteModal';
import ProfileRemindersFormModal, {
  RemindersFormSubmitValues,
} from './ProfileRemindersFormModal';
import ProfileRemindersTable from './ProfileRemindersTable';
import ProfileRemindersTableSkeleton from './ProfileRemindersTableSkeleton';
import { translateKey } from 'src/locales/translate';
import { CARD_HEADER_SX, CARD_HEADER_TITLE_PROPS } from 'src/theme/cardHeader';
import { useTable } from 'src/components/table';
import { TAB_TABLE_DEFAULT_ROWS_PER_PAGE } from 'src/theme/tableContainer';

export function ProfileRemindersContent() {
  const theme = useTheme();
  const { selectedOrganization } = useOrganizationContext();
  const {
    profileId: resolvedProfileId,
    visitId,
    isVisitScoped,
  } = useVisitScope();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [reminderId, setReminderId] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  const handleOnRowClick = (id: string) => {
    setReminderId(id);
    setIsModalOpen(true);
  };

  const handleOpenModal = () => {
    setReminderId('');
    setIsModalOpen(true);
  };

  const handleOnCloseModal = () => {
    setIsModalOpen(false);
    setReminderId('');
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleOpenDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = useTable({
    defaultRowsPerPage: TAB_TABLE_DEFAULT_ROWS_PER_PAGE,
  });

  const SUCCESS_KEYS = {
    create: 'hardcoded.przypomnienie_zostalo_dodane',
    update: 'hardcoded.przypomnienie_zostalo_zaktualizowane',
    delete: 'hardcoded.przypomnienie_zostalo_usuniete',
  } as const;

  const ERROR_KEYS = {
    create: 'hardcoded.wystapil_blad_podczas_dodawania_przypomnienia',
    update: 'hardcoded.wystapil_blad_podczas_aktualizacji_przypomnienia',
    delete: 'hardcoded.wystapil_blad_podczas_usuwania_przypomnienia',
  } as const;

  const {
    items: reminders,
    total,
    isLoading,
    create: createReminder,
    update: updateReminderInScope,
    remove: removeReminder,
    isCreating,
    isUpdating,
    isDeleting,
  } = useRemindersScope({
    page,
    rowsPerPage,
    onMutationSuccess: (kind) => {
      enqueueSnackbar(translateKey(SUCCESS_KEYS[kind]), {
        variant: 'success',
      });
      setIsModalOpen(false);
      if (kind === 'delete') setIsDeleteModalOpen(false);
      if (kind !== 'create') setTimeout(() => setReminderId(''), 500);
    },
    onMutationError: (kind) =>
      enqueueSnackbar(translateKey(ERROR_KEYS[kind]), { variant: 'error' }),
  });

  const deleteReminder = useCallback(
    () => removeReminder(reminderId),
    [removeReminder, reminderId],
  );

  const selectedReminder = reminderId
    ? reminders.find((r) => r.id === reminderId)
    : undefined;

  const canEditSelectedReminder = selectedReminder?.canEdit ?? true;

  const handleOnSubmit = useCallback(
    (values: RemindersFormSubmitValues) => {
      if (reminderId && !canEditSelectedReminder) return;
      if (reminderId) {
        updateReminderInScope(reminderId, values as UpdateReminderDto);
      } else {
        createReminder({
          ...values,
          // On a guest visit the endpoint derives both ids from the visit, so
          // an empty profileId here is expected rather than missing data.
          profileId: resolvedProfileId,
          ...(selectedOrganization?.id
            ? { organizationId: selectedOrganization.id }
            : {}),
          ...(visitId ? { visitId } : {}),
        } as CreateReminderDto);
      }
    },
    [
      canEditSelectedReminder,
      createReminder,
      reminderId,
      resolvedProfileId,
      selectedOrganization,
      updateReminderInScope,
      visitId,
    ],
  );

  return (
    <>
      <Stack sx={CARD_HEADER_SX}>
        {isLoading ? (
          <Skeleton variant="rounded" height="24px" width="112px" />
        ) : (
          <Typography {...CARD_HEADER_TITLE_PROPS}>
            {translateKey('visit.tabReminders')}
          </Typography>
        )}
        {isLoading ? (
          <Skeleton variant="rounded" height="30px" width="77px" />
        ) : (
          <Button
            size="small"
            startIcon={<Iconify icon="eva:plus-fill" width={16} />}
            variant="contained"
            data-testid="reminder-add-open"
            sx={{
              background: theme.palette.common.black,
              color: theme.palette.common.white,
            }}
            onClick={handleOpenModal}
          >
            {translateKey('common.add')}
          </Button>
        )}
      </Stack>

      {isLoading ? (
        <ProfileRemindersTableSkeleton />
      ) : (
        <ProfileRemindersTable
          data={reminders}
          page={page}
          rowsPerPage={rowsPerPage}
          total={total}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
          onRowClick={handleOnRowClick}
        />
      )}

      <ProfileRemindersFormModal
        isModalOpen={isModalOpen}
        closeModal={handleOnCloseModal}
        onSubmit={handleOnSubmit}
        onDelete={handleOpenDeleteModal}
        reminder={selectedReminder}
        canEdit={canEditSelectedReminder}
        isCreating={isCreating}
        isUpdating={isUpdating}
        isDeleting={isDeleting}
        singleReminderOnly={isVisitScoped}
      />

      <ProfileRemindersDeleteModal
        isOpen={isDeleteModalOpen}
        handleClose={handleCloseDeleteModal}
        onDelete={deleteReminder}
        isLoading={isDeleting}
      />
    </>
  );
}
